import React, { useState } from 'react'
import { Info, AlertTriangle, Package } from 'lucide-react'
import { useStoreOutOfStock } from '../lib/data-service'
import { useFiscalWeekContext } from '../contexts/FiscalWeekContext'
import { useTenant } from '../contexts/TenantContext'
import AskAgentButton from './AskAgentButton'

interface StoreOOSTileProps {
  className?: string
}

export function StoreOOSTile({ className = '' }: StoreOOSTileProps) {
  const { selectedFiscalWeek } = useFiscalWeekContext()
  const { selectedRetailerId } = useTenant()
  const { data: oosData, isLoading, error, refetch } = useStoreOutOfStock(selectedFiscalWeek)
  const [showTooltip, setShowTooltip] = useState(false)

  // Format percentage
  const formatPercent = (value: number | null, decimals = 1) => {
    if (value === null) return '—'
    return `${(value * 100).toFixed(decimals)}%`
  }

  // Format currency with compact notation
  const formatCurrency = (value: number | null, compact = true) => {
    if (value === null) return '—'
    if (compact && Math.abs(value) >= 1000) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        notation: 'compact',
        maximumFractionDigits: 1,
      }).format(value)
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value)
  }

  // Get color class based on OOS percentage
  const getOOSColor = (value: number | null) => {
    if (value === null) return 'text-muted-foreground'
    const percent = value * 100
    if (percent >= 10) return 'text-red-600 dark:text-red-400' // danger - high OOS
    if (percent >= 5) return 'text-yellow-600 dark:text-yellow-400' // warning - moderate OOS
    return 'text-green-600 dark:text-green-400' // success - low OOS
  }

  // Loading skeleton
  if (isLoading) {
    return (
      <div className={`card-premium-elevated ${className}`}>
        <div className="animate-pulse">
          <div className="flex items-center justify-between mb-4">
            <div className="h-4 bg-muted rounded w-40"></div>
            <div className="h-4 w-4 bg-muted rounded"></div>
          </div>
          <div className="h-8 bg-muted rounded w-16 mb-3"></div>
          <div className="space-y-2">
            <div className="h-4 bg-muted rounded w-32"></div>
            <div className="h-4 bg-muted rounded w-24"></div>
            <div className="h-3 bg-muted rounded w-20"></div>
          </div>
        </div>
      </div>
    )
  }

  // Error/No Data state
  if (error || !oosData) {
    return (
      <div className={`card-premium-elevated ${className}`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-medium text-foreground">Out-of-Stock % (Stores)</h3>
          <Package className="w-4 h-4 text-gray-400" />
        </div>
        <div className="text-2xl font-bold text-gray-400 mb-2">N/A</div>
        <p className="text-sm text-gray-500 mb-2">Data not provided by retailer</p>
        <p className="text-xs text-gray-400 italic">This metric requires inventory tracking data</p>
        <button 
          onClick={() => refetch()}
          className="text-xs text-primary hover:text-primary/80 underline"
        >
          Retry
        </button>
      </div>
    )
  }

  // Empty state
  if (!oosData || oosData.meta.eligible_rows === 0) {
    return (
      <div className={`card-premium-elevated ${className}`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-medium text-foreground">Out-of-Stock % (Stores)</h3>
          <Info className="w-4 h-4 text-muted-foreground" />
        </div>
        <p className="text-2xl font-bold text-foreground">—</p>
        <p className="text-sm text-muted-foreground">No eligible data</p>
      </div>
    )
  }

  const oosPercent = oosData.oos_percent_current
  const oosPrior = oosData.oos_percent_prior ?? null
  const oosDelta = oosPrior !== null && oosPercent !== null ? oosPercent - oosPrior : null
  const activeStores = oosData.meta?.total_active_stores ?? null
  const activeStoresPrior = oosData.meta?.total_active_stores_prior ?? null
  const activeStoresDelta = activeStoresPrior !== null && activeStores !== null && activeStoresPrior !== 0
    ? (activeStores - activeStoresPrior) / activeStoresPrior
    : null
  const velocityPct = oosData.meta?.velocity_coverage ?? null
  const lostSales = oosData.meta?.estimated_lost_sales ?? null
  const eligibleRows = oosData.meta?.eligible_rows ?? null
  const skippedRows = oosData.meta?.skipped_rows ?? null

  return (
    <div className={`card-premium-elevated ${className}`}>
      {/* Header with Info Icon */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-green-100 rounded-lg">
            <Package className="w-5 h-5 text-green-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">Out-of-Stock % (Stores)</h3>
        </div>
        <div className="flex items-center gap-2">
          {/* Ask Agent Button */}
          <AskAgentButton
            metric="stockout"
            value={oosPercent !== null ? (oosPercent * 100).toFixed(1) : undefined}
            week={selectedFiscalWeek?.toString()}
            retailerId={selectedRetailerId}
            variant="icon"
            size="sm"
            tooltip="Ask AI about stockouts"
          />
          
          <div className="relative">
            <button
              onMouseEnter={() => setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
              onClick={() => setShowTooltip(!showTooltip)}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <Info className="w-5 h-5" />
            </button>
          
            {/* Tooltip */}
            {showTooltip && (
              <div className="absolute top-6 right-0 w-80 p-4 bg-popover border border-border rounded-lg shadow-xl z-50 text-xs">
                <h4 className="font-semibold text-popover-foreground mb-2">How we compute Store OOS%</h4>
                <div className="space-y-2 text-popover-foreground">
                  <p>• Store OOS% is sourced from the curated KPI summary feed for the selected fiscal week (no estimates)</p>
                  <p>• Displayed thresholds: &lt;5% (good), 5–10% (watch), &gt;10% (risk)</p>
                  <p>• Estimated Lost Sales is shown only when the curated KPI feed provides it; otherwise it’s hidden</p>
                  {skippedRows !== null && skippedRows > 0 && (
                    <p className="text-yellow-400">
                      ⚠︎ Skipped {skippedRows} rows due to invalid OOS% or zero demand
                    </p>
                  )}
                  {lostSales === null && velocityPct !== null && velocityPct < 0.5 && (
                    <p className="text-yellow-400">
                      ⚠︎ Lost sales hidden due to insufficient velocity coverage ({Math.round(velocityPct * 100)}%)
                    </p>
                  )}
                </div>
                <p className="text-muted-foreground mt-3 pt-2 border-t border-border">
                  Source: curated KPI summary for the selected week.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Primary Value - OOS Percentage */}
      <div className={`mb-6`}>
        <p className={`text-4xl font-bold ${getOOSColor(oosPercent)}`}>
          {formatPercent(oosPercent)}
        </p>
        <div className="mt-3 text-sm text-gray-600">
          Active Stores: {activeStores !== null ? formatCurrency(activeStores, false) : '—'}
          {activeStoresDelta !== null && (
            <span className={`ml-2 inline-flex items-center px-2 py-0.5 rounded-md border text-xs font-semibold ${activeStoresDelta >= 0 ? 'bg-red-50 text-red-600 border-red-200' : 'bg-green-50 text-green-600 border-green-200'}`}>
              {activeStoresDelta >= 0 ? '▲' : '▼'} {(activeStoresDelta * 100).toFixed(1)}% vs LW
            </span>
          )}
        </div>
      </div>

      {/* Secondary Information */}
      <div className="space-y-4">
        {lostSales !== null && (
          <div className="text-lg text-gray-600 font-semibold">
            Estimated Lost Sales: <span className="font-bold text-gray-900">~{formatCurrency(lostSales)}</span>
          </div>
        )}

        {/* Weight Source Badge */}
        <div className="inline-block px-3 py-2 bg-gray-50 text-gray-700 rounded-lg text-sm font-medium border border-gray-200">
          {velocityPct === null ? 'Source: Curated KPI Summary' : (velocityPct >= 0.99 ? 'Weighted by 5-wk velocity' : 'Velocity + TY Fallback')}
        </div>

        {/* Coverage Note */}
        <div className="text-sm text-gray-600">
          n={eligibleRows ?? '—'}, skipped={skippedRows ?? '—'}
        </div>
      </div>

      {/* Debug Info (development only) */}
      {process.env.NODE_ENV === 'development' && (
        <div className="mt-4 pt-2 border-t border-border text-xs text-muted-foreground">
          Week: {oosData.week} | Eligible: {eligibleRows ?? '—'} | Skipped: {skippedRows ?? '—'} | Velocity: {velocityPct !== null ? `${Math.round(velocityPct * 100)}%` : '—'}
        </div>
      )}
    </div>
  )
}

export default StoreOOSTile
